import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, IconButton, Stack, Tooltip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { FactoryOutlined, GroupsOutlined, HubOutlined, MeetingRoomOutlined } from "@mui/icons-material";
import DataTable from "components/common/tables/DataTable";
import { AppButton } from "components/common/buttons";
import { AppTab, AppTabPanel } from "components/common/tabs";
import AppSnackbar from "components/common/snackbar/AppSnackbar";
import { usePageTitle } from "context/PageTitleContext";
import { PERMISSIONS } from "auth/permissionCodes";
import { usePermissions } from "hooks/usePermissions";
import { userService } from "../../services/user.service";
import { catalogApi, departmentLeaderOptions, normalizeDepartment, normalizeResponseError } from "../../services/catalogs/systemCatalog.service";
import CatalogFormDialog from "./CatalogFormDialog";
import DeleteConfirmDialog from "./DeleteConfirmDialog";
import DepartmentUnitsPanel from "./DepartmentUnitsPanel";

const departmentTypeIcons = {
  PHONG: <MeetingRoomOutlined fontSize="small" />,
  BAN: <HubOutlined fontSize="small" />,
  DOI: <GroupsOutlined fontSize="small" />,
  PHAN_XUONG: <FactoryOutlined fontSize="small" />,
};

const DepartmentsManagement = () => {
  const permissions = usePermissions();
  const canCreate = permissions.can(PERMISSIONS.SYSTEM.DEPARTMENT_CREATE);
  const canUpdate = permissions.can(PERMISSIONS.SYSTEM.DEPARTMENT_UPDATE);
  const canDelete = permissions.can(PERMISSIONS.SYSTEM.DEPARTMENT_DELETE);
  const canReadUnits = permissions.can(PERMISSIONS.SYSTEM.DEPARTMENT_UNIT_READ);
  const canReadUsers = permissions.can(PERMISSIONS.SYSTEM.USER_READ);
  const { setPageTitle } = usePageTitle();
  const [types, setTypes] = useState([]);
  const [activeType, setActiveType] = useState(null);
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 20 });
  const [globalFilter, setGlobalFilter] = useState("");
  const [enterpriseOptions, setEnterpriseOptions] = useState([]);
  const [leaderOptions, setLeaderOptions] = useState([]);
  const [lookupLoading, setLookupLoading] = useState(true);
  const [lookupReady, setLookupReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formState, setFormState] = useState({ open: false, mode: "create", row: null });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const requestVersion = useRef(0);
  const saveInProgress = useRef(false);
  const typeLabel = types.find((type) => type.code === activeType)?.name || "phòng ban";

  const showSnackbar = useCallback((message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  }, []);

  useEffect(() => { setPageTitle("Quản lý phòng ban"); }, [setPageTitle]);

  useEffect(() => {
    let current = true;
    setLookupLoading(true);
    setLookupReady(false);
    Promise.all([
      catalogApi.getDepartmentTypes(),
      catalogApi.list("/Enterprise"),
      canReadUsers ? userService.getAllUsers() : Promise.resolve({ data: [] }),
    ]).then(([departmentTypes, enterprises, usersResponse]) => {
      if (!current) return;
      if (!departmentTypes.length) throw new Error("Không có cấu hình loại phòng ban.");
      setTypes(departmentTypes);
      setActiveType((value) => departmentTypes.some((type) => type.code === value) ? value : departmentTypes[0].code);
      setEnterpriseOptions(enterprises.map((item) => ({ value: item.enterpriseId ?? item.EnterpriseId, label: item.name ?? item.Name })));
      const users = Array.isArray(usersResponse?.data) ? usersResponse.data : [];
      setLeaderOptions(users.map((item) => ({ value: item.userId ?? item.UserId, label: item.fullName || item.FullName || item.userName || item.UserName })));
      setLookupReady(true);
    }).catch((error) => {
      if (current) showSnackbar(normalizeResponseError(error, "Không thể tải cấu hình phòng ban"), "error");
    }).finally(() => { if (current) setLookupLoading(false); });
    return () => { current = false; };
  }, [canReadUsers, showSnackbar]);

  const loadData = useCallback(async () => {
    if (!activeType) return;
    const version = ++requestVersion.current;
    setLoading(true);
    try {
      const result = await catalogApi.page("/Department/search", {
        departmentType: activeType, page: pagination.pageIndex + 1,
        pageSize: pagination.pageSize, keyword: globalFilter || undefined,
      });
      if (version !== requestVersion.current) return;
      if (!result.items.length && pagination.pageIndex > 0) {
        setPagination((value) => ({ ...value, pageIndex: Math.max(0, Math.ceil(result.totalCount / value.pageSize) - 1) }));
        return;
      }
      setRows(result.items.map(normalizeDepartment));
      setRowCount(result.totalCount);
    } catch (error) {
      if (version !== requestVersion.current) return;
      setRows([]);
      setRowCount(0);
      showSnackbar(normalizeResponseError(error, "Không thể tải danh sách phòng ban"), "error");
    } finally {
      if (version === requestVersion.current) setLoading(false);
    }
  }, [activeType, pagination.pageIndex, pagination.pageSize, globalFilter, showSnackbar]);

  useEffect(() => {
    loadData();
    return () => { requestVersion.current += 1; };
  }, [loadData]);

  const fields = useMemo(() => [
    { name: "enterpriseId", label: "Đơn vị", required: true, type: "select", options: enterpriseOptions },
    { name: "departmentType", label: "Loại", required: true, type: "select", disabled: formState.mode === "create", defaultValue: activeType, options: types.map((type) => ({ value: type.code, label: type.name })) },
    { name: "departmentCode", label: "Mã", required: true, maxLength: 20 },
    { name: "departmentName", label: "Tên", required: true, maxLength: 255 },
    { name: "leaderUserId", label: "Người phụ trách", type: "select", options: departmentLeaderOptions(leaderOptions, formState.row) },
    { name: "phone", label: "Số điện thoại", maxLength: 20 },
    { name: "email", label: "Email", maxLength: 100 },
    { name: "description", label: "Mô tả", multiline: true, maxLength: 1000 },
  ], [enterpriseOptions, leaderOptions, types, activeType, formState.mode, formState.row]);

  const handleSubmit = async (values) => {
    if (saveInProgress.current || !lookupReady || (formState.mode === "edit" ? !canUpdate : !canCreate)) return;
    saveInProgress.current = true;
    setSubmitting(true);
    try {
      const payload = {
        enterpriseId: Number(values.enterpriseId),
        departmentType: formState.mode === "create" ? activeType : values.departmentType,
        departmentCode: String(values.departmentCode || "").trim(),
        departmentName: String(values.departmentName || "").trim(),
        leaderUserId: values.leaderUserId ? Number(values.leaderUserId) : null,
        phone: String(values.phone || "").trim() || null,
        email: String(values.email || "").trim() || null,
        description: String(values.description || "").trim() || null,
      };
      if (formState.mode === "edit") {
        await catalogApi.update("/Department", formState.row.departmentId, payload);
        showSnackbar("Cập nhật thành công");
      } else {
        await catalogApi.create("/Department", payload);
        showSnackbar(`Thêm ${typeLabel.toLowerCase()} thành công`);
      }
      setFormState({ open: false, mode: "create", row: null });
      if (payload.departmentType !== activeType) {
        setRows([]);
        setPagination((value) => ({ ...value, pageIndex: 0 }));
        setActiveType(payload.departmentType);
      } else await loadData();
    } catch (error) {
      showSnackbar(normalizeResponseError(error, "Không thể lưu phòng ban"), "error");
    } finally {
      saveInProgress.current = false;
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget || saveInProgress.current || !canDelete) return;
    saveInProgress.current = true;
    setSubmitting(true);
    try {
      await catalogApi.remove("/Department", deleteTarget.departmentId);
      setDeleteTarget(null);
      showSnackbar("Xóa thành công");
      await loadData();
    } catch (error) {
      showSnackbar(normalizeResponseError(error, "Không thể xóa phòng ban"), "error");
    } finally {
      saveInProgress.current = false;
      setSubmitting(false);
    }
  };

  const columns = useMemo(() => [
    { accessorKey: "departmentCode", header: "Mã", size: 100 },
    { accessorKey: "departmentName", header: "Tên", size: 220 },
    { accessorKey: "enterpriseName", header: "Đơn vị", size: 180 },
    { accessorKey: "leaderFullName", header: "Người phụ trách", size: 180, Cell: ({ row }) => row.original.leaderFullName || row.original.leaderUserName || "-" },
    { accessorKey: "phone", header: "Số điện thoại", size: 130 },
    { accessorKey: "email", header: "Email", size: 170 },
    { accessorKey: "description", header: "Mô tả", size: 280 },
  ], []);

  const busy = lookupLoading || loading || submitting;
  return (
    <Box sx={{ p: 2, backgroundColor: "#f9fafb", minHeight: "calc(100vh - 64px)" }}>
      <Stack spacing={2}>
        <AppTab value={activeType ?? false} aria-label="Loại phòng ban"
          tabs={types.map((type) => ({ value: type.code, label: type.name, icon: departmentTypeIcons[type.code], disabled: submitting || formState.open || Boolean(deleteTarget) }))}
          onChange={(_, value) => {
            setRows([]);
            setRowCount(0);
            setPagination((current) => ({ ...current, pageIndex: 0 }));
            setActiveType(value);
          }} />
        <AppTabPanel value={activeType} index={activeType}>
          <DataTable key={activeType} columns={columns} data={rows} getRowId={(row) => String(row.departmentId)}
            manualPagination manualFiltering rowCount={rowCount}
            state={{ isLoading: lookupLoading || loading, showProgressBars: submitting, pagination, globalFilter }}
            onPaginationChange={setPagination}
            onGlobalFilterChange={(value) => { setGlobalFilter(value); setPagination((current) => ({ ...current, pageIndex: 0 })); }}
            enableRowActions={canUpdate || canDelete}
            enableExpanding={canReadUnits}
            renderDetailPanel={canReadUnits ? ({ row }) => <DepartmentUnitsPanel department={row.original} leaderOptions={leaderOptions} onSnackbar={showSnackbar} /> : undefined}
            renderTopToolbarExtraActions={() => canCreate ? (
              <AppButton variant="contained" startIcon={<AddIcon />} disabled={busy || !lookupReady || !activeType}
                onClick={() => setFormState({ open: true, mode: "create", row: null })}>
                Thêm {typeLabel.toLowerCase()}
              </AppButton>
            ) : null}
            renderRowActions={({ row }) => (
              <Stack direction="row" spacing={0.5} justifyContent="center">
                {canUpdate && <Tooltip title="Sửa"><IconButton aria-label="Sửa" size="small" color="primary" disabled={busy || !lookupReady}
                  onClick={() => setFormState({ open: true, mode: "edit", row: row.original })}><EditIcon fontSize="small" /></IconButton></Tooltip>}
                {canDelete && <Tooltip title="Xóa"><IconButton aria-label="Xóa" size="small" color="error" disabled={busy}
                  onClick={() => setDeleteTarget(row.original)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>}
              </Stack>
            )} />
        </AppTabPanel>
      </Stack>
      <CatalogFormDialog open={formState.open}
        title={formState.mode === "edit" ? `Cập nhật ${typeLabel.toLowerCase()}` : `Thêm ${typeLabel.toLowerCase()}`}
        fields={fields} initialValues={formState.row} submitting={submitting}
        onClose={() => { if (!submitting) setFormState({ open: false, mode: "create", row: null }); }} onSubmit={handleSubmit} />
      <DeleteConfirmDialog open={Boolean(deleteTarget)} submitting={submitting}
        message={`Bạn có chắc chắn muốn xóa “${deleteTarget?.departmentName || ""}”?`}
        onClose={() => { if (!submitting) setDeleteTarget(null); }} onConfirm={handleDelete} />
      <AppSnackbar open={snackbar.open} message={snackbar.message} severity={snackbar.severity}
        onClose={() => setSnackbar((current) => ({ ...current, open: false }))} />
    </Box>
  );
};

export default DepartmentsManagement;
